/*
  Girls Know product page behaviour.
  <gk-gallery>  thumbnail rail switches the main image, and follows the variant
                when Dawn's product-info publishes a variant change.
  <gk-ugc-wall> opens a social post in a lightbox with its caption and tagged products.
  gk-play       starts the impact band's video and hands over to native controls.
  <gk-sticky-atc> reveals the fixed add-to-cart bar once the in-page button scrolls away.
*/
if (!customElements.get('gk-gallery')) {
  customElements.define(
    'gk-gallery',
    class GkGallery extends HTMLElement {
      connectedCallback() {
        this.thumbs = Array.from(this.querySelectorAll('[data-gk-thumb]'));
        this.slides = Array.from(this.querySelectorAll('[data-gk-slide]'));
        if (!this.slides.length) return;

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => this.show(thumb.dataset.mediaId));
        });

        // keep the gallery in step with the variant picker when one is present.
        // PUB_SUB_EVENTS is a const in constants.js, so it is a global binding
        // rather than a property of window — check it by identifier.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            const mediaId = data?.variant?.featured_media?.id;
            if (mediaId) this.show(String(mediaId));
          });
        }
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      show(mediaId) {
        if (!mediaId) return;
        const slide = this.slides.find((s) => s.dataset.mediaId === mediaId);
        if (!slide) return;

        this.slides.forEach((s) => {
          s.classList.toggle('is-active', s === slide);
          // pause any video that scrolls out of view
          s.querySelectorAll('video').forEach((video) => {
            if (s !== slide && !video.paused) video.pause();
          });
        });
        this.thumbs.forEach((thumb) => {
          thumb.setAttribute('aria-current', thumb.dataset.mediaId === mediaId ? 'true' : 'false');
        });
      }
    }
  );
}

if (!customElements.get('gk-sticky-atc')) {
  customElements.define(
    'gk-sticky-atc',
    class GkStickyAtc extends HTMLElement {
      connectedCallback() {
        this.bar = this.querySelector('[data-gk-sticky-bar]');
        const watched = document.getElementById(this.dataset.gkWatch || '');
        if (!this.bar) return;

        // no in-page button to watch (it was removed from the blocks): just show it
        if (!watched || !('IntersectionObserver' in window)) {
          this.toggle(true);
          return;
        }

        this.observer = new IntersectionObserver(
          ([entry]) => this.toggle(!entry.isIntersecting),
          { rootMargin: '0px 0px -40px 0px' }
        );
        this.observer.observe(watched);

        this.onResize = () => {
          if (this.bar.classList.contains('is-visible')) this.reserveSpace(true);
        };
        window.addEventListener('resize', this.onResize);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        window.removeEventListener('resize', this.onResize);
        this.reserveSpace(false);
      }

      toggle(visible) {
        this.bar.classList.toggle('is-visible', visible);
        this.reserveSpace(visible);
      }

      // the bar is fixed, so without this it sits on top of whatever ends the page
      reserveSpace(visible) {
        document.body.style.paddingBottom = visible ? `${this.bar.offsetHeight}px` : '';
      }
    }
  );
}

if (!customElements.get('gk-ugc-wall')) {
  customElements.define(
    'gk-ugc-wall',
    class GkUgcWall extends HTMLElement {
      connectedCallback() {
        // a tile with a caption or a tagged product opens the post in a lightbox
        this.addEventListener('click', this.onClick);
        const dialog = this.dialog;
        if (dialog) {
          dialog.addEventListener('click', this.onDialogClick);
          dialog.addEventListener('close', this.onDialogClose);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.dialog?.removeEventListener('close', this.onDialogClose);
      }

      get dialog() {
        return document.getElementById(this.dataset.gkLightbox || '');
      }

      onClick = (event) => {
        const trigger = event.target.closest('[data-gk-ugc-open]');
        if (!trigger || !this.contains(trigger)) return;
        event.preventDefault();
        this.open(trigger);
      };

      onDialogClick = (event) => {
        if (event.target.closest('[data-gk-lightbox-close]') || event.target === event.currentTarget) {
          event.currentTarget.close();
        }
      };

      onDialogClose = (event) => {
        event.currentTarget.querySelectorAll('.gk-ugcbox').forEach((post) => {
          post.classList.remove('is-open');
          post.hidden = true;
        });
        document.documentElement.classList.remove('gk-modal-open');
        const trigger = this.lastTrigger;
        this.lastTrigger = null;
        if (trigger && document.contains(trigger)) trigger.focus();
      };

      open(trigger) {
        const dialog = this.dialog;
        const post = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!dialog || !post) return;

        dialog.querySelectorAll('.gk-ugcbox').forEach((other) => {
          other.classList.remove('is-open');
          other.hidden = true;
        });

        this.lastTrigger = trigger;
        post.hidden = false;
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
        document.documentElement.classList.add('gk-modal-open');
        requestAnimationFrame(() => post.classList.add('is-open'));
      }
    }
  );
}

if (!customElements.get('gk-review-rail')) {
  customElements.define(
    'gk-review-rail',
    class GkReviewRail extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-gk-review-list]');
        this.dots = Array.from(this.querySelectorAll('[data-gk-review-dot]'));
        if (!this.list || this.dots.length < 2) return;

        this.dots.forEach((dot, i) => {
          dot.addEventListener('click', () => {
            const card = this.list.children[i];
            if (card) this.list.scrollTo({ left: card.offsetLeft - this.list.offsetLeft, behavior: 'smooth' });
          });
        });

        // the list is only a scroller on phones; above that it is a masonry and
        // no scroll events fire, which is fine — the dots are hidden there
        this.onScroll = this.debounce(() => {
          const width = this.list.clientWidth;
          if (!width) return;
          this.setActive(Math.round(this.list.scrollLeft / width));
        }, 80);
        this.list.addEventListener('scroll', this.onScroll);
      }

      disconnectedCallback() {
        this.list?.removeEventListener('scroll', this.onScroll);
      }

      debounce(fn, wait) {
        let timer;
        return (...args) => {
          clearTimeout(timer);
          timer = setTimeout(() => fn(...args), wait);
        };
      }

      setActive(index) {
        const current = Math.max(0, Math.min(index, this.dots.length - 1));
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === current ? 'true' : 'false'));
      }
    }
  );
}

// impact band: the overlaid play button starts the video, then gets out of the way
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-gk-play]');
  if (!button) return;
  const media = button.closest('[data-gk-media]');
  const video = media?.querySelector('video');
  if (!video) return;

  video.controls = true;
  media.classList.add('is-playing');
  video.play();
  video.addEventListener('pause', () => media.classList.remove('is-playing'), { once: true });
});

